import type { RVLayout } from '../layout-managers/LayoutManager';
import type { ViewabilityConfig } from './ViewabilityConfig';

/**
 * Web port of upstream FlashList's ViewabilityHelper
 * (src/recyclerview/viewability/ViewabilityHelper.ts).
 *
 * One helper owns one (config, callback) pair. The engine computes a candidate
 * "possibly viewable" index set on each scroll (typically the strict
 * viewport-intersecting indices, without overscan buffer) and hands it in via
 * `updateViewableItems`. The helper applies the threshold rules, debounces by
 * `minimumViewTime`, and only fires the callback when the resulting set
 * differs from the last reported one.
 *
 * Divergences from upstream:
 *   - We don't depend on `react-native` for the `ViewabilityConfig` type — see
 *     `./ViewabilityConfig.ts` for the local mirror.
 *   - The callback shape is `(indices, newlyVisible, newlyNonVisible)` —
 *     identical to upstream. The wrapping into ViewToken<T>[] happens one
 *     level up in `ViewabilityManager`.
 */
export class ViewabilityHelper {
  hasInteracted = false;

  private viewableIndices: number[] = [];
  private lastReportedViewableIndices: number[] = [];

  private readonly viewabilityConfig: ViewabilityConfig | null | undefined;
  private readonly viewableIndicesChanged: (
    indices: number[],
    newlyVisibleIndices: number[],
    newlyNonVisibleIndices: number[],
  ) => void;

  private readonly timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(
    viewabilityConfig: ViewabilityConfig | null | undefined,
    viewableIndicesChanged: (
      indices: number[],
      newlyVisibleIndices: number[],
      newlyNonVisibleIndices: number[],
    ) => void,
  ) {
    this.viewabilityConfig = viewabilityConfig;
    this.viewableIndicesChanged = viewableIndicesChanged;
  }

  dispose(): void {
    this.timers.forEach(clearTimeout);
    this.timers.clear();
  }

  updateViewableItems(
    horizontal: boolean,
    scrollOffset: number,
    listSize: { width: number; height: number },
    getLayout: (index: number) => RVLayout | undefined,
    possiblyViewableIndices: number[],
  ): void {
    const config = this.viewabilityConfig;
    if (
      config?.itemVisiblePercentThreshold != null &&
      config?.viewAreaCoveragePercentThreshold != null
    ) {
      throw new Error(
        'FlashList: viewabilityConfig.itemVisiblePercentThreshold and ' +
          'viewabilityConfig.viewAreaCoveragePercentThreshold are mutually ' +
          'exclusive — set one or the other.',
      );
    }
    if ((config?.waitForInteraction ?? false) && !this.hasInteracted) {
      return;
    }

    const newViewableIndices = possiblyViewableIndices.filter(index =>
      this.isItemViewable(
        index,
        horizontal,
        scrollOffset,
        listSize,
        config?.viewAreaCoveragePercentThreshold,
        config?.itemVisiblePercentThreshold,
        getLayout,
      ),
    );
    this.viewableIndices = newViewableIndices;

    // Default 250ms matches upstream / RN VirtualizedList. 0 is a valid
    // explicit opt-out, mostly for tests.
    const minimumViewTime = config?.minimumViewTime ?? 250;
    if (minimumViewTime > 0) {
      const timeoutId = setTimeout(() => {
        this.timers.delete(timeoutId);
        this.checkViewableIndicesChanges(newViewableIndices);
      }, minimumViewTime);
      this.timers.add(timeoutId);
    } else {
      this.checkViewableIndicesChanges(newViewableIndices);
    }
  }

  /**
   * Diffs the candidate against `viewableIndices` (still-viewable now, after
   * any debounce) and against the last reported set; only fires if there's a
   * real delta.
   */
  checkViewableIndicesChanges(newViewableIndices: number[]): void {
    const currentlyViewable = newViewableIndices.filter(index =>
      this.viewableIndices.includes(index),
    );
    const newlyVisible = currentlyViewable.filter(
      index => !this.lastReportedViewableIndices.includes(index),
    );
    const newlyNonVisible = this.lastReportedViewableIndices.filter(
      index => !currentlyViewable.includes(index),
    );

    if (newlyVisible.length === 0 && newlyNonVisible.length === 0) {
      return;
    }
    this.lastReportedViewableIndices = currentlyViewable;
    this.viewableIndicesChanged(
      currentlyViewable,
      newlyVisible,
      newlyNonVisible,
    );
  }

  clearLastReportedViewableIndices(): void {
    this.lastReportedViewableIndices = [];
  }

  private isItemViewable(
    index: number,
    horizontal: boolean,
    scrollOffset: number,
    listSize: { width: number; height: number },
    viewAreaCoveragePercentThreshold: number | null | undefined,
    itemVisiblePercentThreshold: number | null | undefined,
    getLayout: (index: number) => RVLayout | undefined,
  ): boolean {
    const itemLayout = getLayout(index);
    if (itemLayout === undefined) return false;

    const itemStart = (horizontal ? itemLayout.x : itemLayout.y) - scrollOffset;
    const itemExtent = horizontal ? itemLayout.width : itemLayout.height;
    const viewportExtent = horizontal ? listSize.width : listSize.height;
    const pixelsVisible =
      Math.min(itemStart + itemExtent, viewportExtent) - Math.max(itemStart, 0);

    // Fully visible always counts.
    if (pixelsVisible === itemExtent) return true;
    // Not visible at all → out.
    if (pixelsVisible <= 0) return false;

    const useViewAreaMode = viewAreaCoveragePercentThreshold != null;
    const ratio = useViewAreaMode
      ? pixelsVisible / viewportExtent
      : pixelsVisible / itemExtent;
    const threshold = useViewAreaMode
      ? viewAreaCoveragePercentThreshold * 0.01
      : (itemVisiblePercentThreshold ?? 0) * 0.01;
    return ratio >= threshold;
  }
}
